import * as React from 'react';
import { Requirement } from '../../../models';
import { FLEX_COLUMN, FLEX_ROW } from '../../../../layout/flexBox';
import LocalDiv from '../../../../localization/LocalDiv';
import { css } from '@emotion/core';
import CircleImg from '../../../../layout/CircleImg';
import { faUserPlus, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import Spacer from '../../../../layout/Spacer';
import Metric from '../../../../layout/Metric';
import { Str } from '../../../../localization/translationKeys';
import { Link } from 'react-router-dom';
import { Routes } from '../../../../navigation/routes';
import { countInvited, countIncomplete } from '../../../requirements/module';
import { COLORS } from '../../../../globalStyle/colors';
import Msg from '../../../../localization/Msg';


const styles = {
  root:        css`
    justify-content: space-between;
  `,
  raterRow:    css`
    justify-content: center;
  `,
  metric:      css`
    width: 100px;
    flex-basis: 100px;
    color: ${COLORS.DARK_GREY};
  `,
  metricValue: css`
    font-size: 33px;
  `,
  metricLabel: css`
    font-size: 12px;
  `,
  link:        css`
    font-size: 12px;
    text-align: center;
    text-decoration: none;
  `,
};

type Props = {
  routes: Routes;
  surveyPastDue: boolean;
  inviteRatersEnabled: boolean;
  requirements: Requirement[] | undefined;
  className?: string;
};


/**
 * RaterCounts: a component that shows the number of incomplete/invited raters for the given project
 *
 * PureComponent: true
 * Redux Connected: false
 */
const RaterCounts: React.FC<Props> = props => {
  const invited    = React.useMemo(() => countInvited(props.requirements), [props.requirements]);
  const incomplete = React.useMemo(() => countIncomplete(props.requirements), [props.requirements]);

  const allowLinking = React.useMemo(() => !props.surveyPastDue && props.inviteRatersEnabled, [props]);

  const inviteLink = React.useMemo(() => allowLinking ? props.routes.InviteRaters : '#', [allowLinking, props.routes]);

  const remindLink = React.useMemo(
    () => allowLinking ? props.routes.RemindRatersProjDash : '#',
    [allowLinking, props.routes],
  );

  const color = React.useMemo(() => allowLinking ? COLORS.GREEN : COLORS.DISABLED, [allowLinking]);

  const linkStyle = React.useMemo(
    () => css`
      color: ${color}; 
      cursor: ${allowLinking ? 'pointer' : 'default'};
      margin-top: 5px;
    
      &:hover {
        text-decoration: ${allowLinking ? 'underline' : 'none'};
      }
    `,
    [color, allowLinking],
  );

  const circleStyle = React.useMemo(
    () => css`
      background-color: ${color};
      cursor: ${allowLinking ? 'pointer' : 'default'};
      transition: all .2s ease;
      font-size: 20px;
      
      &:hover {
        background-color: ${allowLinking ? COLORS.HOVER_GREY : color};
      }
    `,
    [color, allowLinking],
  );

  return (
    <LocalDiv css={[FLEX_COLUMN, styles.root]} className={props.className}>
      <LocalDiv css={[FLEX_ROW, styles.raterRow]}>
        <Link
          to={inviteLink}
          aria-disabled={allowLinking}
          css={styles.link}
        >
          <CircleImg icon={faUserPlus} css={circleStyle} />
        </Link>
        <Spacer />
        <Metric
          value={invited}
          label={Str.Invited}
          defaultLabel='Invited'
          labelStyle={styles.metricLabel}
          valueStyle={styles.metricValue}
          css={styles.metric}
        >
          {
            props.inviteRatersEnabled && (
              <Link to={inviteLink} css={[styles.link, linkStyle]}>
                <Msg str={Str.InviteRatersParen} />
              </Link>
            )
          }
        </Metric>
      </LocalDiv>
      <LocalDiv css={[FLEX_ROW, styles.raterRow]}>
        <Link
          to={remindLink}
          aria-disabled={allowLinking}
          css={styles.link}
        >
          <CircleImg
            icon={faExclamationTriangle}
            css={[circleStyle, allowLinking && css`background-color: ${COLORS.MEDIUM_GREEN};`]}
          />
        </Link>
        <Spacer />
        <Metric
          value={incomplete}
          label={Str.Incomplete}
          defaultLabel='Incomplete'
          labelStyle={styles.metricLabel}
          valueStyle={styles.metricValue}
          css={styles.metric}
        >
          {
            props.inviteRatersEnabled && (
              <Link to={remindLink} css={[styles.link, linkStyle]}>
                <Msg str={Str.RemindRatersParen} />
              </Link>
            )
          }
        </Metric>
      </LocalDiv>
    </LocalDiv>
  );
};

export default RaterCounts;
